import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import moment from 'moment';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import RichTextEditor from '@/shared/components/RichTextEditor';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import CanRead from '@/shared/components/security/CanRead';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { LinkIcon } from '@/shared/components/Icons';
import AuthService, { privileges } from '@/shared/services/AuthService';

import api from '@/shared/api';
import config from '@/config';
import DataCacheService from '@/shared/services/DataCacheService';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import DatePicker from '@/shared/components/input/DatePicker';
import FormGroupPart from '@/shared/components/registration/partials/FormGroupPart';
import TimeInput from '@/shared/components/input/TimeInput';
import UserSearch from '@/shared/components/input/UserSearch';

export const isBeforeToday = date => {
  if (!moment.isMoment(date)) return false;
  return moment(date).isBefore(moment.utc(), 'day');
};

const messages = defineMessages({
  ContactInformation: 'Contact information',
  RoleAdminPulse: 'Role AdminPulse',
  RoleOnDocument: 'Role on document',
  OnlyEmailsByAdminPulseCanBeResent: 'E-mails that were not sent by AdminPulse cannot be resent',
  TheMailHasBeenSentAgain: 'The email has been sent again',
  ResendEmail: 'Resend email',
  ResendEmailConfirmationMsg: 'Are you sure you want to resend this email?',
  Share: 'Share',
  MarkAsSigned: 'Mark as signed',
  Template: 'Template',
  CreateRelation: 'Create relation',
  UpdateRelation: 'Update relation',
  Download: 'Download',
  Forward: 'Forward',
  Open: 'Open',
  LinkToRelation: 'Link to relation',
  DeleteDocument: 'Delete document',
  ShowAllDocumentsForRelation: 'Show documents for relation',
  EmailStatus: 'Email status',
  RemoveEmails: 'Remove e-mails',
  EmailGlobalActionsTooltip:
    'Select one or more e-mails to execute a global action on these e-mails',
  DropzoneExplanation:
    'AdminPulse will try to identify documents uploaded here and suggest a way to process them.',
  AutoLinkDocumentsToCompanyExplanation:
    'The document will be linked to this company. If new relations are created based on this document, the relations will be linked to this company.',
  DocumentWillBeLinkedToCompanyAutomatically: 'The document will be linked to company {company}.',
  DocumentWillBeLinkedToRelationAutomatically:
    'Documents that you upload here will now be automatically linked to {relation}.',
  Documents: 'Documents',
  Dropzone: 'Dropzone',
  ChooseRelationLinkDocument: 'Choose a relation you want to link {documentName} to.',
  NoDocumentsFound: 'No documents found',
  UploadFilesToCurrentFolder: 'Drop to upload files to current folder',
  UploadFilesToSpecificFolder: 'Drop to upload files to a specific folder',
  MoveOrCopyDocument: 'Drop your document to move or copy it',
  DropDocumentToAddTag: 'Drop your document to add a tag',
  NrOfLinkedRelations: '{nr} relations',
  AllDocumentsSelected: 'All documents selected',
  AllEmailsSelected: 'All emails selected',
  CountEmailsSelected: 'Selected {count} e-mails',
  Name: 'Name',
  Relation: 'Relation',
  Relations: 'Relations',
  Edit: 'Edit',
  Type: 'Type',
  Status: 'Status',
  SearchDocuments: 'Search documents',
  IncludeInactiveRelations: 'Include inactive relations',
  AllDocuments: 'All documents',
  DocumentsWithoutTags: 'Without tags',
  AllTags: 'All tags',
  Tags: 'Tags',
  Folders: 'Folders',
  FolderStructure: 'Folder structure',
  WithDocumentTags: 'With document tags',
  WithoutDocumentTags: 'Without document tags',
  // Toastrs
  MsgDeleteDocumentSuccess: 'Document deleted',
  MsgDeleteDocumentError: 'Error while deleting document',

  MsgLinkRelationDocumentSuccess: 'Document linked to relation',
  MsgLinkRelationDocumentError: 'Error while linking document to relation',

  MsgRecognitionDocumentUploadError: 'Error while uploading recognition document',

  MsgMarkSignedLetterSuccess: 'Document marked as signed assignment letter',
  MsgMarkSignedLetterError: 'Error while marking assignment letter as signed',

  MsgCreateRelationSuccess: 'Relation created',
  MsgCreateRelationError: 'Error while creating relation',

  MsgUpdateRelationSuccess: 'Relation updated',
  MsgUpdateRelationError: 'Error while updating relation',
  DocumentDate: 'Document date',
  CreationDate: 'Creation date',
  CreatedBy: 'Created by',
  Role: 'Role',

  AmountOfAuthorizations: 'Amount of authorizations',
  AmountOfRelations: 'Amount of relations',
  AmountOfRegistrations: 'Amount of registrations',
  UpdateAuthorizations: 'Update authorizations',
  Importregistrationlist: 'Import registrations',
  ProcessAuthorizationList: 'Process authorization list',
  CreateNewRelations: 'Create relation if it does not exist',
  RemoveExpiredAuthorizations: 'Remove expired authorizations',
  ProcessAuthorizationListQueued:
    'Authorization list will be processed shortly. Click here to view its progress.',
  ProcessRegistrationListQueued:
    'Registration import will be processed shortly. Click here to view its progress.',
  NotLinkedToARelation: 'Not linked to a relation',
  NotLinkedToARelationShort: 'No relation',
  LinkedToMultipleRelations: 'Linked to multiple relations',
  LinkedToMultipleRelationsShort: 'Multiple relations',
  GlobalActions: 'Global actions',
  DocumentGlobalActionsTooltip:
    'Select one or more documents to execute a global action on these documents.',
  AddOrRemoveTags: 'Add or remove tags',
  Action: 'Action',
  AddTags: 'Add tags',
  RemoveTags: 'Remove tags',
  EnterTag: 'Enter tag',
  GlobalActionStarted: 'Global action was started, click here to see its progress',
  AddOrRemoveRelations: 'Add or remove relations',
  AddRelations: 'Add relations',
  RemoveRelations: 'Remove relations',
  DeleteSingleDocumentConfirmationMsg:
    'Are you sure you want to delete this document? This action cannot be undone.',
  DeleteMultipleDocumentsConfirmationMsg:
    'Are you sure you want to delete these documents? This action cannot be undone.',
  RemoveDocuments: 'Remove documents',
  DownloadAsZip: 'Download as zip',
  DownloadZipWithFolderStructureYesNo:
    'Do you want to download the documents in their folder structure? If not, they will be added to the zip as one plain list of documents.',
  CountDocumentsSelected: 'Selected {count} documents',
  SendByEmail: 'Send by email',
  PleaseFilterOnRelationBeforeSendingEmail:
    'Please select a relation before trying to send an email.',
  EmailSent: 'Email sent',
  PleaseMakeManualSelectionOfDocuments:
    'Please make a manual selection of documents that you want to send by email, not using the select-all button.',
  ResetFilters: 'Reset filters',
  DocumentWasMoved: 'The document was moved.',
  SigningStatus: 'Signing status',
  CreateNewToDo: 'Create new to-do',
  DocumentIsPublicOnPortal: 'Document is public on portal',
  EnableOrDisableIsPublicOnPortal: 'Set or remove is public on portal',
  IsPublicOnPortal: 'Is public on portal',
  SendSignatureRequest: 'Send signature request',
  NoDocumentProviderConfigured:
    'No signing provider has been configured. You can configure a document signing provider under general settings.',

  NoSignaturedFoundOnDocument: 'No signature fields were found on the document.',
  Signature: 'Signature',
  Signer: 'Signer',
  Email: 'Email',
  Mobile: 'Mobile',
  ChooseEmployee: 'Choose an employee',
  ChooseClient: 'Choose a client',
  Or: 'Or',
  NoEmailAddress: 'No email address',
  NoPhone: 'No phone',
  SelfSignature: 'Self signature',
  RequiredSignature: 'Signature is required',
  DocumentSigningRequestSent: 'Signature request sent',
  DocumentSigningRequestFailed: 'Signature request failed',
  TheDocumentNeedsToBeLinkedToARelationInOrderToSign:
    'The document needs to be linked to at least 1 relation in order to send a signature request.',
  ChooseRelation: 'Choose relation',
  ClickToAddSignatureFieldsToDocumentOnEmptyPage:
    'Click on the button to start adding signees to the document. The signatures will be placed on an empty page at the end of the document.',

  Add: 'Add',
  Delete: 'Delete',
  RevokeSignatureRequest: 'Revoke signature request',
  RevokeSignatureRequestConfirmationMsg: 'Are you sure you want to revoke this signature request?',
  SignatureRequestRevoked: 'Signature request revoked',
  DocumentCreatedBy: 'Created by',
  NoRelationCandidatesFound:
    'No e-mailaddress or contact with an e-mailaddress was found. Please add an e-mailaddress to the relation, or add a linked relation with an e-mailaddress if the relation is of type company.',
  DeleteRelationLink: 'Delete relation link',
  DeleteRelationLinkConfirmationMsg:
    'Are you sure you want to delete the relation from this document?',
  RelationLinkDeleted: 'Relation link deleted.',
  ChangeDocumentDate: 'Change document date',
  InvalidDate: 'Invalid date',
  SendOn: 'Send on',
  DelayedSendingExplanation: 'If empty, the e-mail will be sent immediately',
  Emails: 'E-mails',
  Attachments: 'Attachments',
  Invoices: 'Invoices',
  IncludeEmailsTooltip: 'Show or hide emails in the document overview',
  IncludeInvoicesTooltip: 'Show or hide invoices in the document overview',
  IncludeAttachmentsTooltip: 'Show or hide attachments in the document overview',
  CreateMergedPdf: 'Create merged PDF',
  Subject: 'Subject',
  EmailTo: 'To',
  From: 'From',
  Cc: 'CC',
  Bcc: 'BCC',
  Date: 'Date',
  ToCcBcc: 'To / CC / BCC',
  SystemEmailsCannotBeRemoved: 'Invoice e-mails that were sent by AdminPulse cannot be removed',
  NoEmailsFound: 'No emails found',
  DocumentWasUploaded:
    'Document uploaded. It will be added to the list when recognition has finished',
  UploadFilesToStartRecognition: 'Upload files to start recognition',
  ColumnConfig: 'Configure columns',
  SelectOverviewColumns: 'Select overview columns',
  DocumentDateWasModified: 'Document date was modified',
  InvalidDocumentDate: 'Invalid document date',
  ClickForAdvancedUpload: 'Click for advanced upload',
  Content: 'Content',
  NoDigitalSigningTemplateFound: 'No digital signing template found',
  ChooseTemplate: 'Choose template',
  OpenEmailTemplates: 'Open email templates',
  Templates: 'Templates',
  AddSigner: 'Add signer',
  AddCc: 'Add Cc',
  SigningOrder: 'Order',
  MoveUp: 'Move up',
  MoveDown: 'Move down',
  CustomSigningOrder: 'Custom signing order',
  RequiredEmailBodyAndSubject: 'Email body and subject are required',
  RequiredCcSigner: 'CC recipient is required',
  Signers: 'Signers',
  DownloadAsPdf: 'Download as pdf',
  RepresentativeOfRelation: '(representative of {relation})',
  SendViaEmail: 'Send via e-mail',
  SendAsPdf: 'Send PDF via e-mail',
  SelectRelation: 'Select relation',
  DocumentCouldNotBeConverted: 'Document could not be converted',
  OneOrMoreOfTheDocumentsCannotBeSigned: 'OneOrMoreOfTheDocumentsCannotBeSigned',
  Time: 'Time',
});

class DocumentSignatureRequestModal extends Component {
  signingProvidersWithCustomEmail = [3, 5, 6, 7];

  state = {
    noProviderConfigured: false,
    scheduledDate: undefined,
    scheduledTime: '00:00',
    employeeCandidates: [],
    relationCandidates: [],
    relations: [],
    companyId: undefined,
    documentSigningProvider: undefined,
    useSigningOrder: false,
    signersCc: [],
    appendCcsToDocument: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
    documents: PropTypes.arrayOf({}).isRequired,
  };

  static defaultProps = {};

  componentDidMount() {
    const { documents, intl } = this.props;

    for (let i = 0; i < documents.length; i++) {
      const canSign =
        AuthService.hasPermission({ functionality: 'Relation', privilege: 'Update' }) &&
        documents[i].canBeSigned &&
        documents[i].signingStatus < 20 && // not signed
        documents[i].signingStatus !== 10; // not pending
      if (!canSign) {
        this.setState({ cannotSign: true });
        break;
      }
    }

    const relations = documents.flatMap(x => x.relations);
    this.setState({ relations, includeRole: DataCacheService.hasUserConnectPlatform(23) });

    const dataCache = DataCacheService.getDataCache();
    const relationCompanyIds =
      relations && relations.length
        ? new Set(relations.map(x => x.companyId))
        : new Set([dataCache.defaultcompany.id]);
    const companyId =
      relationCompanyIds.size === 1 ? [...relationCompanyIds][0] : dataCache.defaultcompany.id;

    this.setState({
      companyId,
    });

    const company = dataCache.company.find(x => x.id === companyId) || dataCache.defaultcompany;
    const { documentSigningProvider } = company;
    this.setState({ documentSigningProvider });

    // no provider configured
    if (documentSigningProvider === 0) {
      this.setState({ noProviderConfigured: true });
    } else if (relations && relations.length) {
      this.setState({ loading: true });

      const emptyPromise = () => Promise.resolve();
      // if Penneo is the signing provider, we dont need to find signature fields because Penneo doesn't support these markers
      const signaturesPromise =
        documentSigningProvider === 5
          ? emptyPromise
          : api.base.post(
              `${config.appUrls.documentsConverter}/api/getsigningfields/${documentSigningProvider}`,
              documents.map(x => x.id),
            );

      const templatesPromise = this.signingProvidersWithCustomEmail.includes(
        documentSigningProvider,
      )
        ? api.base.get(`${config.appUrls.api}/api/template/company/15`)
        : emptyPromise;

      Promise.all([signaturesPromise, templatesPromise])
        .then(([{ data: signers }, { data: templates }]) => {
          let updatedState = this.state;
          updatedState = {
            ...updatedState,
            signingProvider: documentSigningProvider,
            documentHasMarkers: !!signers,
            templates,
          };

          if (signers) {
            updatedState = {
              ...updatedState,
              signers,
            };
          }

          if (!templates || templates.length === 0) {
            // retrieve default email template
            api.base
              .get(
                `${
                  config.appUrls.documentSigning
                }/api/DefaultEmailTemplate/${documentSigningProvider}/${relations[0].languageCode ||
                  'nl'}/${company.id}/${encodeURIComponent(documents[0].name)}`,
              )
              .then(
                ({ data }) => {
                  updatedState = {
                    ...updatedState,
                    subject: data.subject,
                    initialBody: data.body,
                    body: data.body,
                  };
                  this.setState(updatedState, () => this.retrieveSigningCandidates());
                },
                error => {
                  const { intl } = this.props;
                  handleHttpError(intl)(error);
                  this.setState(updatedState, () => this.retrieveSigningCandidates());
                },
              );
          } else if (templates.length === 1) {
            // select only template and load content
            updatedState = {
              ...updatedState,
              selectedTemplate: templates[0].id,
            };
            this.setState(updatedState, () =>
              this.retrieveDigitalSigningEmail(this.retrieveSigningCandidates),
            );
          } else {
            this.setState(updatedState, () => this.retrieveSigningCandidates());
          }
        })
        .catch(error => {
          handleHttpError(intl)(error);
          this.setState({ loading: false });
          this.closeModal();
        });
    }
  }

  templateChanged = event => {
    const template = event.target.value;

    this.setState(
      state => ({
        ...state,
        selectedTemplate: template,
      }),
      () => this.retrieveDigitalSigningEmail(),
    );
  };

  retrieveDigitalSigningEmail = callback => {
    const { intl, documents } = this.props;
    const { selectedTemplate, relations } = this.state;
    const relation = relations[0];
    this.setState({ loadingEmail: true });
    return api.base
      .get(
        `${config.appUrls.api}/api/template/GetDigitalSigningMail?relationId=${relation.id}&templateId=${selectedTemplate}&filename=${documents[0].name}`,
      )
      .then(
        ({ data }) => {
          this.setState(
            {
              subject: data.subject,
              initialBody: data.body,
              body: data.body,
              loadingEmail: false,
            },
            () => (callback ? callback() : {}),
          );
        },
        error => {
          this.setState({ loading: false });
          handleHttpError(intl)(error);
          this.setState(
            {
              subject: '',
              initialBody: '',
              body: '',
              loadingEmail: false,
            },
            () => (callback ? callback() : {}),
          );
        },
      );
  };

  retrieveSigningCandidates = () => {
    const { relations } = this.state;

    this.setState({ loading: true });

    const relationIds = relations.map(x => x.id);

    api.base
      .post(`${config.appUrls.api}/api/RelationDocument/GetSigningCandidates`, relationIds)
      .then(({ data }) => {
        if (data) {
          let { employeeCandidates, relationCandidates } = data;
          if (!!relationCandidates)
            relationCandidates.sort((a, b) => a.fullName.localeCompare(b.fullName));
          employeeCandidates = (employeeCandidates || [])
            .sort((a, b) => a.fullName.localeCompare(b.fullName))
            .map(x => ({ id: x.id, name: x.fullName, ...x }));
          this.setState({
            employeeCandidates,
            relationCandidates,
            loading: false,
          });
        }
      })
      .catch(error => {
        this.setState({
          loading: false,
        });
        const { intl } = this.props;
        handleHttpError(intl)(error);
      });
  };

  employeeChanged = (selection, index) => {
    let employeeId = !!selection ? selection.id : null;
    let employee = !!selection ? this.state.employeeCandidates.find(e => e.id === employeeId) : {};

    const { signers } = this.state;

    this.setState({
      signers: signers.map((x, idx) => {
        if (index === idx) {
          return {
            ...x,
            ...employee,
            signerId: employeeId,
            employeeId,
            relationId: null,
          };
        }
        return x;
      }),
    });
  };

  relationCandidateChanged = (event, index) => {
    const relationId = event.target.value;
    if (!relationId) return;

    const { relationCandidates, signers } = this.state;

    const relation = relationCandidates.find(x => x.id === relationId);
    this.setState({
      signers: signers.map((x, idx) => {
        if (idx === index) {
          return {
            ...x,
            ...relation,
            signerId: relationId,
            employeeId: null,
            relationId,
          };
        }
        return x;
      }),
    });
  };

  signerRoleChanged = (event, index) => {
    const value = event.target.value;
    const { signers } = this.state;

    this.setState({
      signers: signers.map((x, idx) => {
        if (idx === index) {
          return {
            ...x,
            role: value,
          };
        }
        return x;
      }),
    });
  };

  moveSignerUp = index => {
    if (index <= 0) return;
    this.setState(({ signers }) => {
      const updated = [...signers];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      return { signers: updated };
    });
  };

  signerCcEmployeeChanged = (selection, index) => {
    const employeeId = selection ? selection.id : null;
    const employee = selection ? this.state.employeeCandidates.find(e => e.id === employeeId) : {};
    this.setState(({ signersCc }) => ({
      signersCc: signersCc.map((x, idx) =>
        idx === index ? { ...x, ...employee, signerId: employeeId } : x,
      ),
    }));
  };

  addSignerCc = () => {
    this.setState(({ signersCc }) => ({
      appendCcsToDocument: true,
      signersCc: [...signersCc, { signerId: null }],
    }));
  };

  removeSignerCc = index => {
    this.setState(({ signersCc }) => ({
      signersCc: signersCc.filter((_, idx) => idx !== index),
    }));
  };

  moveSignerDown = index => {
    this.setState(({ signers }) => {
      if (index >= signers.length - 1) return null;
      const updated = [...signers];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      return { signers: updated };
    });
  };

  signerIsSelfSignature = signer => {
    if (!signer || !signer.fields) return false;
    for (let i = 0; i < signer.fields.length; i++) {
      if (signer.fields[i].isSelfSignature) {
        return true;
      }
    }

    return false;
  };

  validate = () => {
    const { signers, body, subject, documentSigningProvider, signersCc } = this.state;
    const { intl } = this.props;

    if (!signers || signers.length === 0) {
      ToastrService.error(intl.formatMessage(messages.RequiredSignature));
      return false;
    }

    for (let i = 0; i < signers.length; i++) {
      const requiredSignature = signers[i];
      if (!this.signerIsSelfSignature(requiredSignature) && !requiredSignature.signerId) {
        ToastrService.error(intl.formatMessage(messages.RequiredSignature));
        return false;
      }
    }

    for (let i = 0; i < signersCc.length; i++) {
      if (!signersCc[i].signerId) {
        ToastrService.error(intl.formatMessage(messages.RequiredCcSigner));
        return false;
      }
    }

    if (
      this.signingProvidersWithCustomEmail.includes(documentSigningProvider) &&
      (!body || !subject)
    ) {
      ToastrService.error(intl.formatMessage(messages.RequiredEmailBodyAndSubject));
      return false;
    }

    return true;
  };

  handleDateChange(date) {
    const { scheduledTime } = this.state;
    const combinedDateTime = this.combineDateAndTime(date, scheduledTime);
    this.setState({ scheduledDate: combinedDateTime });
  }

  handleTimeChange(time) {
    const { scheduledDate } = this.state;
    const combinedDateTime = this.combineDateAndTime(scheduledDate, time);
    this.setState({ scheduledDate: combinedDateTime, scheduledTime: time });
  }

  combineDateAndTime(date, time) {
    if (!date || !time || time === '00:00') return date;
    const hoursTime = Math.floor(time / 60);
    const minutesTime = time % 60;
    const timeString = `${hoursTime.toString().padStart(2, '0')}:${minutesTime
      .toString()
      .padStart(2, '0')}`;
    const [hours, minutes] = timeString.split(':');
    date
      .hour(hours)
      .minute(minutes)
      .second(0)
      .millisecond(0);
    return date;
  }

  onSave = () => {
    if (!this.validate()) return;
    const {
      signers,
      signersCc,
      companyId,
      relations,
      appendSignaturesToDocument,
      scheduledDate,
      subject,
      body,
      documentSigningProvider,
    } = this.state;
    const { documents, onSave, intl } = this.props;

    const relation = relations[0];
    const signingRequest = {
      name: documents[0].name,
      emailSubject: subject,
      emailBody: body,
      relationId: relation.id,
      documentLanguage: relation.languageCode || 'nl',
      companyId,
      initiator: AuthService.getEmail(),
      signingProvider: documentSigningProvider,
      appendSignaturesToDocument,
      scheduledDate,
      documents: [
        ...documents.map(x => ({ documentId: x.id, fileName: x.name, contentType: x.mime })),
      ],
      signers:
        documentSigningProvider === 5
          ? signers.map((s, i) => ({ ...s, signOrder: this.state.useSigningOrder ? i + 1 : 0 }))
          : signers,
      cc: signersCc.map(x => x.emailAddress),
    };

    this.setState({ sending: true, error: null });

    return api.base
      .post(`${config.appUrls.documentSigning}/api/CreateDocumentSigningRequest`, signingRequest)
      .then(({ data }) => {
        this.setState({ false: true });
        ToastrService.success(intl.formatMessage(messages.DocumentSigningRequestSent));
        const updatedDocuments = documents.map(x => ({
          ...x,
          signingId: data.signingId,
          signingPackageId: data.signingPackageId,
          signingStatus: data.signingStatus,
        }));
        return onSave(updatedDocuments);
      })
      .catch(error => {
        this.setState({ sending: false });

        if (error.response && error.response.status === 400) {
          if (error.response.data && error.response.data.code) {
            // the Penneo custom exception where we notify the user about the missing {link} in the email content is seen as a translation parameter
            if (error.response.data.code === 503) {
              error.response.data.parameters = {
                ...error.response.data.parameters,
                link: '{link}',
              };
            }
            handleHttpError(intl)(error);
          } else {
            ToastrService.error(intl.formatMessage(messages.DocumentSigningRequestFailed));
            this.setState({ error: JSON.stringify(error.response.data, null, 2) });
          }
        } else {
          handleHttpError(intl)(error);
        }
      });
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  render() {
    const { show, intl, stateService } = this.props;
    const {
      noProviderConfigured,
      employeeCandidates,
      relationCandidates,
      signers,
      loading,
      error,
      documentHasMarkers,
      appendSignaturesToDocument,
      signingProvider,
      scheduledDate,
      dateFocused,
      subject,
      templates,
      initialBody,
      selectedTemplate,
      loadingEmail,
      relations,
      cannotSign,
      includeRole,
      useSigningOrder,
      signersCc,
      appendCcsToDocument,
    } = this.state;
    const signerCcEnabled = signingProvider === 3;
    if (!show) return null;

    const noRelationsLinkedToDocument = !relations || !relations.length;

    const signingProvidersWithDelayedSending = [3, 5, 6];

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.SendSignatureRequest)}
        show={show}
        onClose={this.closeModal}
        disabled={noProviderConfigured || noRelationsLinkedToDocument || cannotSign}
        onSave={() => this.onSave()}
        size="xl"
      >
        <div className="form-horizontal">
          <div>
            {!loading && noProviderConfigured && (
              <div
                className="alert alert-danger"
                role="alert"
                style={{ paddingTop: '2px', paddingBottom: '2px' }}
              >
                <div className="margin5T">
                  <FormattedMessage {...messages.NoDocumentProviderConfigured} />
                </div>
              </div>
            )}
            {!loading && cannotSign && (
              <div
                className="alert alert-danger"
                role="alert"
                style={{ paddingTop: '2px', paddingBottom: '2px' }}
              >
                <div className="margin5T">
                  <FormattedMessage {...messages.OneOrMoreOfTheDocumentsCannotBeSigned} />
                </div>
              </div>
            )}
            {!loading &&
              !noProviderConfigured &&
              !noProviderConfigured &&
              noRelationsLinkedToDocument && (
                <div
                  className="alert alert-danger"
                  role="alert"
                  style={{ paddingTop: '2px', paddingBottom: '2px' }}
                >
                  <div className="margin5T">
                    <FormattedMessage
                      {...messages.TheDocumentNeedsToBeLinkedToARelationInOrderToSign}
                    />
                  </div>
                </div>
              )}
            {!loading && relationCandidates && relationCandidates.length === 0 && (
              <div
                className="alert alert-warning"
                role="alert"
                style={{ paddingTop: '2px', paddingBottom: '2px' }}
              >
                <div className="margin5T">
                  <FormattedMessage {...messages.NoRelationCandidatesFound} />
                </div>
              </div>
            )}

            {!loading && (
              <>
                <div className="form-group">
                  <label
                    className="col-md-1 control-label"
                    style={{ textAlign: 'left', paddingTop: '12px' }}
                  >
                    <FormattedMessage {...messages.Signers} />
                  </label>
                  {signingProvider === 5 && (
                    <div className="col-md-4" style={{ paddingTop: '5px' }}>
                      <label style={{ fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={useSigningOrder}
                          onChange={e => this.setState({ useSigningOrder: e.target.checked })}
                        />{' '}
                        {intl.formatMessage(messages.CustomSigningOrder)}
                      </label>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <div className="col-md-12">
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: '0 8px',
                      }}
                    >
                      <thead>
                        <tr>
                          {documentHasMarkers && (
                            <th className="col-md-1 padding0L">
                              <FormattedMessage {...messages.Signature} />
                            </th>
                          )}

                          <th className="padding0L">
                            <FormattedMessage {...messages.Signer} />
                          </th>
                          <th className="col-md-2 padding0L">
                            <FormattedMessage {...messages.ContactInformation} />
                          </th>

                          <th className="col-md-2 padding0L">
                            {includeRole ? (
                              <FormattedMessage {...messages.RoleAdminPulse} />
                            ) : (
                              <FormattedMessage {...messages.Role} />
                            )}
                          </th>
                          {includeRole ? (
                            <th className="col-md-2 padding0L">
                              <FormattedMessage {...messages.RoleOnDocument} />
                            </th>
                          ) : null}
                          {useSigningOrder && (
                            <th
                              className="padding0L"
                              style={{ width: '50px', textAlign: 'center' }}
                            >
                              <FormattedMessage {...messages.SigningOrder} />
                            </th>
                          )}
                          {(appendSignaturesToDocument || useSigningOrder) && (
                            <th style={{ width: '1%', whiteSpace: 'nowrap' }} />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {signers
                          ? signers.map((signer, index) => (
                              <tr key={`${index}`}>
                                {documentHasMarkers && <td>{signer.signerIdentifier}</td>}
                                {!this.signerIsSelfSignature(signer) && (
                                  <td>
                                    <div style={{ display: 'flex' }}>
                                      <UserSearch
                                        users={employeeCandidates}
                                        onChange={e => this.employeeChanged(e, index)}
                                        value={signer.employeeId}
                                        placeholder={intl.formatMessage(messages.ChooseEmployee)}
                                      />
                                      <div style={{ display: 'table' }}>
                                        <span
                                          style={{
                                            display: 'table-cell',
                                            verticalAlign: 'middle',
                                            padding: '5px',
                                          }}
                                        >
                                          {intl.formatMessage(messages.Or)}
                                        </span>
                                      </div>

                                      <select
                                        onChange={e => this.relationCandidateChanged(e, index)}
                                        className="form-control"
                                        id="documentSignatureModal-RelationCandidates"
                                        style={{
                                          display: 'inline',
                                          width: '45%',
                                          marginTop: '7px',
                                        }}
                                        value={
                                          signer.relationId ||
                                          intl.formatMessage(messages.ChooseClient)
                                        }
                                      >
                                        {!signer.relationId && (
                                          <option value="">
                                            {intl.formatMessage(messages.ChooseClient)}
                                          </option>
                                        )}
                                        {relationCandidates.map(relation => (
                                          <option key={relation.id} value={relation.id}>
                                            {relation.fullName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </td>
                                )}
                                {!this.signerIsSelfSignature(signer) && (
                                  <td>
                                    {signer.signerId && (
                                      <>
                                        <span className="display-block">
                                          {signer.emailAddress && (
                                            <span className="text-success text-nowrap">
                                              <i className="fa fa-check" />
                                              {signer.emailAddress}
                                            </span>
                                          )}
                                          {!signer.emailAddress && (
                                            <span className="text-danger text-nowrap">
                                              <i className="fa fa-times" />
                                              <FormattedMessage {...messages.NoEmailAddress} />
                                            </span>
                                          )}
                                        </span>
                                        <span className="display-block">
                                          {signer.phone && (
                                            <span className="text-success text-nowrap">
                                              <i className="fa fa-check" />
                                              {signer.phone}
                                            </span>
                                          )}
                                          {!signer.phone && (
                                            <span className="text-danger text-nowrap">
                                              <i className="fa fa-times" />
                                              <FormattedMessage {...messages.NoPhone} />
                                            </span>
                                          )}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                )}

                                {!this.signerIsSelfSignature(signer) && (
                                  <td>
                                    {signer.signerId &&
                                      signer.roles &&
                                      signer.roles.map(role => (
                                        <span>
                                          {!role.linkType && role.role >= 0 ? (
                                            <span>
                                              {intl.formatMessage(listItemMsg('Role', role.role))}
                                            </span>
                                          ) : null}
                                          {!role.role && role.linkType && role.linkType >= 0 ? (
                                            <div>
                                              {intl.formatMessage(
                                                listItemMsg('linkTypeto', role.linkType),
                                              )}
                                              {role.position && role.position >= 0 ? (
                                                <span>
                                                  {' ('}
                                                  {intl.formatMessage(
                                                    listItemMsg(
                                                      'companyownerposition',
                                                      role.position,
                                                    ),
                                                  )}
                                                  )
                                                </span>
                                              ) : null}
                                              {role.representative ? (
                                                <>
                                                  &nbsp;
                                                  {intl.formatMessage(
                                                    messages.RepresentativeOfRelation,
                                                    { relation: role.representsRelation },
                                                  )}
                                                </>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </span>
                                      ))}
                                  </td>
                                )}

                                {this.signerIsSelfSignature(signer) && (
                                  <td colSpan="3">
                                    <FormattedMessage {...messages.SelfSignature} />
                                  </td>
                                )}
                                {includeRole ? (
                                  <td>
                                    {signer.signerId && (
                                      <input
                                        type="text"
                                        value={signer.role || ''}
                                        className="form-control"
                                        onChange={e => this.signerRoleChanged(e, index)}
                                        placeholder={intl.formatMessage(messages.RoleOnDocument)}
                                      />
                                    )}
                                  </td>
                                ) : null}
                                {useSigningOrder && (
                                  <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                )}
                                {(appendSignaturesToDocument || useSigningOrder) && (
                                  <td style={{ paddingLeft: '10px', whiteSpace: 'nowrap' }}>
                                    {useSigningOrder && (
                                      <>
                                        <a
                                          onClick={() => this.moveSignerUp(index)}
                                          disabled={index === 0}
                                          title={intl.formatMessage(messages.MoveUp)}
                                          className="btn btn-default btn-xs"
                                        >
                                          <i className="fa fa-arrow-up fa-fw" />
                                        </a>
                                        <a
                                          onClick={() => this.moveSignerDown(index)}
                                          disabled={index === signers.length - 1}
                                          title={intl.formatMessage(messages.MoveDown)}
                                          className="btn btn-default btn-xs"
                                        >
                                          <i className="fa fa-arrow-down fa-fw" />
                                        </a>
                                      </>
                                    )}
                                    {appendSignaturesToDocument && (
                                      <a
                                        onClick={() =>
                                          this.setState({
                                            signers: signers.filter((item, idx) => idx !== index),
                                          })
                                        }
                                        title={intl.formatMessage(messages.Delete)}
                                        className="btn btn-default btn-xs"
                                      >
                                        <i className="fa fa-trash fa-fw" />
                                      </a>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))
                          : null}
                      </tbody>
                      <tbody>
                        <tr>
                          <td colSpan={8}>
                            {!noProviderConfigured &&
                              !cannotSign &&
                              !noRelationsLinkedToDocument &&
                              !documentHasMarkers &&
                              (!signers || signers.length === 0) && (
                                <React.Fragment>
                                  <div
                                    className="alert alert-info"
                                    role="alert"
                                    style={{ paddingTop: '2px', paddingBottom: '2px' }}
                                  >
                                    {signingProvider !== 5 && (
                                      <div className="margin5T">
                                        <FormattedMessage
                                          {...messages.NoSignaturedFoundOnDocument}
                                        />
                                      </div>
                                    )}
                                    <div className="margin5T">
                                      <FormattedMessage
                                        {...messages.ClickToAddSignatureFieldsToDocumentOnEmptyPage}
                                      />
                                    </div>
                                  </div>
                                </React.Fragment>
                              )}
                            {!noProviderConfigured &&
                              !noRelationsLinkedToDocument &&
                              !documentHasMarkers &&
                              !cannotSign && (
                                <div className="margin5T">
                                  <a
                                    onClick={() =>
                                      this.setState({
                                        appendSignaturesToDocument: true,
                                        signers: [
                                          ...(signers || []),
                                          {
                                            marker: null,
                                            fields: [{ type: 1, isSelfSignature: false }],
                                            signer: null,
                                            signerId: null,
                                          },
                                        ],
                                      })
                                    }
                                    className="btn btn-default"
                                  >
                                    <i className="fa fa-plus fa-fw" />
                                    {intl.formatMessage(messages.AddSigner)}
                                  </a>
                                  {signingProvider === 3 &&
                                    (!signers ||
                                      !signers.some(x => this.signerIsSelfSignature(x))) && (
                                      <a
                                        onClick={() =>
                                          this.setState({
                                            appendSignaturesToDocument: true,
                                            signers: [
                                              ...(signers || []),
                                              {
                                                marker: null,
                                                fields: [{ type: 1, isSelfSignature: true }],
                                                signer: null,
                                                signerId: null,
                                              },
                                            ],
                                          })
                                        }
                                        className="btn btn-default margin5L"
                                      >
                                        <i className="fa fa-plus fa-fw" />
                                        {intl.formatMessage(messages.SelfSignature)}
                                      </a>
                                    )}
                                </div>
                              )}
                          </td>
                        </tr>
                      </tbody>
                      {signerCcEnabled && (
                        <tbody>
                          <tr>
                            {documentHasMarkers && <td />}
                            <td colSpan={7} style={{ paddingLeft: 0, fontWeight: 'bold' }}>
                              Cc
                            </td>
                          </tr>
                          {signersCc.map((cc, index) => (
                            <tr key={`cc-${index}`}>
                              {documentHasMarkers && <td />}
                              <td>
                                <UserSearch
                                  users={employeeCandidates}
                                  onChange={e => this.signerCcEmployeeChanged(e, index)}
                                  value={cc.signerId}
                                  placeholder={intl.formatMessage(messages.ChooseEmployee)}
                                />
                              </td>
                              <td />
                              <td />
                              {includeRole && <td />}
                              {useSigningOrder && <td />}
                              {appendCcsToDocument && (
                                <td
                                  style={{
                                    paddingLeft: '10px',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                  }}
                                >
                                  <a
                                    onClick={() => this.removeSignerCc(index)}
                                    title={intl.formatMessage(messages.Delete)}
                                    className="btn btn-default btn-xs"
                                  >
                                    <i className="fa fa-trash fa-fw" />
                                  </a>
                                </td>
                              )}
                            </tr>
                          ))}
                          {(appendSignaturesToDocument || documentHasMarkers) && (
                            <tr>
                              {documentHasMarkers && <td />}
                              <td colSpan={7}>
                                <div className="margin5T">
                                  <a
                                    onClick={this.addSignerCc}
                                    className="btn btn-default margin5L"
                                  >
                                    <i className="fa fa-plus fa-fw" />
                                    {intl.formatMessage(messages.AddCc)}
                                  </a>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      )}
                    </table>
                  </div>
                </div>
              </>
            )}

            {signingProvidersWithDelayedSending.includes(signingProvider) && (
              <div className="form-group margin10T">
                <label
                  className="col-md-1 control-label"
                  style={{ textAlign: 'left', paddingTop: '12px' }}
                >
                  <FormattedMessage {...messages.SendOn} />
                </label>
                <div className="col-md-1 margin5T">
                  <DatePicker
                    date={this.state.scheduledDate}
                    placeholder={intl.formatMessage(messages.DelayedSendingExplanation)}
                    onDateChange={date => this.handleDateChange(date)}
                    focused={this.state.dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id="scheduledDate"
                    required
                    block
                    small
                    isOutsideRange={isBeforeToday}
                  />
                </div>
                <div className="col-md-4 margin5T">
                  <FormGroupPart label={messages.Time}>
                    <TimeInput
                      value={this.state.scheduledTime} // Time state
                      id="ScheduledTime"
                      disabled={!this.state.scheduledDate}
                      onChange={time => this.handleTimeChange(time)} // Function to handle time changes
                    />
                  </FormGroupPart>
                </div>
              </div>
            )}

            {this.signingProvidersWithCustomEmail.includes(signingProvider) && (
              <React.Fragment>
                <div className="form-group">
                  <label
                    className="control-label col-md-1"
                    style={{ textAlign: 'left', paddingTop: '12px' }}
                  >
                    {intl.formatMessage(messages.Template)}
                  </label>
                  <div className="col-md-9">
                    {!templates.length && (
                      <label className="control-label">
                        {intl.formatMessage(messages.NoDigitalSigningTemplateFound)}
                      </label>
                    )}
                    {templates.length > 0 && (
                      <select
                        onChange={this.templateChanged}
                        className="form-control"
                        value={selectedTemplate || intl.formatMessage(messages.ChooseTemplate)}
                      >
                        {!selectedTemplate && (
                          <option value="">{intl.formatMessage(messages.ChooseTemplate)}</option>
                        )}
                        {templates
                          .map(template => ({
                            ...template,
                            translation: intl.formatMessage(
                              listItemMsg('template', template.id, template.name),
                            ),
                          }))
                          .sort((a, b) =>
                            a.translation.toLowerCase().localeCompare(b.translation.toLowerCase()),
                          )
                          .map(template => (
                            <option key={template.id} value={template.id}>
                              {template.translation}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                  <span className="col-md-2">
                    <CanRead on="settings">
                      <button
                        type="button"
                        className="btn btn-xs btn-default btn-block"
                        title={intl.formatMessage(messages.OpenEmailTemplates)}
                        onClick={() => stateService.goToEmailTemplates()}
                      >
                        <LinkIcon className="margin5R" />
                        <FormattedMessage {...messages.Templates} />
                      </button>
                    </CanRead>
                  </span>
                </div>

                {loadingEmail && <LoadingAnimation />}
                {!loadingEmail && (
                  <div className="form-group">
                    <label
                      className="col-md-1 control-label"
                      style={{ textAlign: 'left', paddingTop: '12px' }}
                    >
                      <FormattedMessage {...messages.Subject} />
                    </label>
                    <div className="col-md-9">
                      <input
                        name="subject"
                        required
                        className="form-control"
                        maxLength="500"
                        value={subject}
                        onChange={e => this.setState({ subject: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                {!loadingEmail && (
                  <div className="form-group">
                    <label
                      className="col-md-1 control-label"
                      style={{ textAlign: 'left', paddingTop: '12px' }}
                    >
                      <FormattedMessage {...messages.Content} />
                    </label>
                    <div className="col-md-9">
                      <RichTextEditor
                        initialValue={initialBody}
                        onChange={newBody => this.setState({ body: newBody })}
                        base64Images
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}

            {loading && <LoadingAnimation />}

            {error && (
              <div
                className="alert alert-danger"
                role="alert"
                style={{ paddingTop: '2px', paddingBottom: '2px' }}
              >
                <div className="margin5T">{error}</div>
              </div>
            )}
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
export default injectStateService(DocumentSignatureRequestModal);
